import {
  parsePatientDate,
  resolvePriorAuthGridStatus,
  type PatientEligibility,
  type PatientRecord,
  type PatientStatus,
} from '../data/patientData'
import type { ApiEligibilityCheck } from './eligibilityChecksService'

function toText(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return null
}

function toSnakeCaseKey(key: string): string {
  return key.replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`)
}

function pickField(record: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const direct = toText(record[key])
    if (direct) return direct
    const snake = toSnakeCaseKey(key)
    const snakeValue = toText(record[snake])
    if (snakeValue) return snakeValue
  }
  return null
}

function looksLikeStediSubscriber(record: Record<string, unknown>): boolean {
  const memberId = pickField(record, 'memberId')
  const groupNumber = pickField(record, 'groupNumber')
  const groupDescription = pickField(record, 'groupDescription')
  const firstName = pickField(record, 'firstName')
  const lastName = pickField(record, 'lastName')
  return Boolean(memberId && (groupNumber || groupDescription || (firstName && lastName)))
}

function deepFindStediSubscriber(
  value: unknown,
  depth = 0,
  seen = new Set<unknown>()
): Record<string, unknown> | null {
  if (depth > 14 || value == null || typeof value !== 'object') return null
  if (seen.has(value)) return null
  seen.add(value)

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = deepFindStediSubscriber(item, depth + 1, seen)
      if (found) return found
    }
    return null
  }

  const record = value as Record<string, unknown>
  if (looksLikeStediSubscriber(record)) return record

  for (const child of Object.values(record)) {
    const found = deepFindStediSubscriber(child, depth + 1, seen)
    if (found) return found
  }
  return null
}

function toRecord(value: unknown): Record<string, unknown> {
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value) as unknown
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>
      }
    } catch {
      return {}
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function toDateText(value: unknown): string | null {
  const text = toText(value)
  if (!text) return null
  const date = parsePatientDate(text)
  if (!date) return null
  return date.toISOString().slice(0, 10)
}

function normalizeEligibility(status: unknown): PatientEligibility {
  const value = typeof status === 'string' ? status.toLowerCase() : ''
  if (value.includes('verified') || value === 'success' || value === 'completed') return 'verified'
  if (value.includes('expire')) return 'expires_soon'
  if (value.includes('lapse') || value.includes('denied') || value.includes('inactive')) return 'lapsed'
  return 'pending'
}

function normalizePatientStatus(status: unknown): PatientStatus {
  const value = typeof status === 'string' ? status.toLowerCase() : ''
  if (value.includes('hold')) return 'on_hold'
  if (value.includes('inactive')) return 'inactive'
  if (value.includes('pending')) return 'pending'
  return 'active'
}

function normalizeGender(value: unknown): string | undefined {
  const text = toText(value)
  if (!text) return undefined
  const lower = text.toLowerCase()
  if (text === 'M' || lower === 'male' || lower === 'm') return 'Male'
  if (text === 'F' || lower === 'female' || lower === 'f') return 'Female'
  if (lower === 'other' || lower === 'u' || lower === 'unknown') return 'Other'
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

function normalizeRelationship(value: unknown): string | undefined {
  const text = toText(value)
  if (!text) return undefined
  const lower = text.toLowerCase()
  if (
    lower.includes('insured or subscriber') ||
    (lower.includes('subscriber') && !lower.includes('dependent'))
  ) {
    return 'Self'
  }
  if (lower === 'self' || lower === '18' || lower.includes('self')) return 'Self'
  if (lower === 'spouse' || lower === '01' || lower.includes('spouse')) return 'Spouse'
  if (
    lower === 'child' ||
    lower === '19' ||
    lower.includes('child') ||
    lower.includes('dependent') ||
    lower.includes('son') ||
    lower.includes('daughter')
  ) {
    return 'Child'
  }
  if (lower === 'other') return 'Other'
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

function normalizeCoordinationOfBenefits(value: unknown): string | undefined {
  const text = toText(value)
  if (!text) return undefined
  const lower = text.toLowerCase()
  if (lower.includes('secondary')) return 'Secondary'
  if (lower.includes('tertiary')) return 'Tertiary'
  if (lower.includes('primary')) return 'Primary'
  return text
}

function pverifySubscriberName(subscriber: Record<string, unknown>): string {
  const fullName = toText(subscriber.FullName)
  if (fullName) return fullName
  return [toText(subscriber.Firstname), toText(subscriber.Lastname_R)].filter(Boolean).join(' ').trim()
}

function formatPverifyAddress(subscriber: Record<string, unknown>): string | undefined {
  const address = toRecord(subscriber.Address) ?? toRecord(subscriber.AddressInfo)
  if (address && Object.keys(address).length > 0) {
    return formatAddress(address)
  }
  const inline = [
    toText(subscriber.AddressLine1) ?? toText(subscriber.Address1),
    toText(subscriber.AddressLine2) ?? toText(subscriber.Address2),
    toText(subscriber.City),
    toText(subscriber.State),
    toText(subscriber.Zip),
  ]
    .filter(Boolean)
    .join(', ')
  return inline || undefined
}

function formatAddress(address: Record<string, unknown>): string | undefined {
  const line1 = toText(address.address1) ?? toText(address.address_1)
  const line2 = toText(address.address2) ?? toText(address.address_2)
  const city = toText(address.city)
  const state = toText(address.state)
  const postal = toText(address.postalCode) ?? toText(address.postal_code) ?? toText(address.zip)
  const cityStateZip = [city, state].filter(Boolean).join(', ')
  const cityLine = [cityStateZip, postal].filter(Boolean).join(' ')
  const parts = [line1, line2, cityLine].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : undefined
}

function firstPlanDetail(responseInner: Record<string, unknown>): string | null {
  const planStatus = responseInner.planStatus ?? responseInner.plan_status
  if (!Array.isArray(planStatus) || planStatus.length === 0) return null
  for (const item of planStatus) {
    const record = toRecord(item)
    const detail = pickField(record, 'planDetails', 'plan_details')
    if (detail) return detail
  }
  return null
}

function hasStediEligibilityShape(value: Record<string, unknown>): boolean {
  return Boolean(
    value.subscriber ||
      value.payer ||
      value.planInformation ||
      value.planDateInformation ||
      value.planStatus ||
      value.benefitsInformation
  )
}

/** Stedi 271 body may live on `response`, `response_payload.response`, or nested `payload`. */
function resolveStediEligibilityBody(
  check: ApiEligibilityCheck,
  responsePayload: Record<string, unknown>,
  responsePayloadInner: Record<string, unknown>
): Record<string, unknown> {
  const candidates = [
    toRecord(check.response),
    toRecord(responsePayload.response),
    toRecord(responsePayloadInner.response),
    responsePayloadInner,
    responsePayload,
  ]
  for (const candidate of candidates) {
    if (hasStediEligibilityShape(candidate)) return candidate
  }
  return toRecord(check.response)
}

function firstNonEmptyRecord(
  ...candidates: Array<Record<string, unknown> | undefined>
): Record<string, unknown> {
  for (const candidate of candidates) {
    if (candidate && Object.keys(candidate).length > 0) return candidate
  }
  return {}
}

export function mapEligibilityCheckToPatientRecord(
  check: ApiEligibilityCheck,
  index = 0
): PatientRecord | null {
  const requestPayload = toRecord(check.request_payload)
  const responsePayload = toRecord(check.response_payload)
  const requestPayloadInner = toRecord(requestPayload.payload)
  const responsePayloadInner = toRecord(responsePayload.payload)
  const responseInner = resolveStediEligibilityBody(check, responsePayload, responsePayloadInner)

  const requestPayer = toRecord(requestPayload.payer)
  const requestPayloadInnerPayer = toRecord(requestPayloadInner.payer)
  const responsePayer = toRecord(responsePayload.payer)
  const responsePayloadInnerPayer = toRecord(responsePayloadInner.payer)
  const responseInnerPayer = toRecord(responseInner.payer)

  const requestPatientPayload = toRecord(requestPayload.patient)
  const responsePatientPayload = toRecord(responsePayload.patient)
  const requestSubscriberPayload = toRecord(requestPayload.subscriber)
  const responseSubscriberPayload = toRecord(responsePayload.subscriber)
  const requestPayloadInnerPatient = toRecord(requestPayloadInner.patient)
  const responsePayloadInnerPatient = toRecord(responsePayloadInner.patient)
  const requestPayloadInnerSubscriber = toRecord(requestPayloadInner.subscriber)
  const responsePayloadInnerSubscriber = toRecord(responsePayloadInner.subscriber)
  const deepSubscriber = deepFindStediSubscriber(check)
  const responseSubscriber = firstNonEmptyRecord(
    toRecord(responseInner.subscriber),
    toRecord(responsePayloadInner.subscriber),
    toRecord(responsePayload.subscriber),
    toRecord(responseSubscriberPayload),
    toRecord(responsePayloadInnerSubscriber),
    deepSubscriber ?? undefined
  )
  const responsePatient = firstNonEmptyRecord(
    toRecord(responseInner.patient),
    toRecord(responsePayloadInner.patient),
    toRecord(responsePayload.patient),
    toRecord(responsePatientPayload),
    toRecord(responsePayloadInnerPatient)
  )
  const responseProvider = firstNonEmptyRecord(
    toRecord(responseInner.provider),
    toRecord(responsePayloadInner.provider),
    toRecord(responsePayload.provider)
  )
  const planInformation = firstNonEmptyRecord(
    toRecord(responseInner.planInformation),
    toRecord(responsePayloadInner.planInformation),
    toRecord(responsePayload.planInformation)
  )
  const planDateInformation = firstNonEmptyRecord(
    toRecord(responseInner.planDateInformation),
    toRecord(responsePayloadInner.planDateInformation),
    toRecord(responsePayload.planDateInformation)
  )
  const subscriberAddress = toRecord(responseSubscriber.address)
  const pverifyDemographic =
    toRecord(responsePayload.DemographicInfo) ??
    toRecord(responsePayloadInner.DemographicInfo) ??
    toRecord(responseInner.DemographicInfo)
  const pverifySubscriber = toRecord(pverifyDemographic?.Subscriber)
  const pverifyPlanSummary =
    toRecord(responsePayload.PlanCoverageSummary) ??
    toRecord(responsePayloadInner.PlanCoverageSummary) ??
    toRecord(responseInner.PlanCoverageSummary)

  const firstName =
    toText(check.first_name) ??
    toText(check.patient_first_name) ??
    toText(check.subscriber_first_name) ??
    toText(responseSubscriber.firstName) ??
    toText(responseSubscriberPayload.firstName) ??
    toText(responsePayloadInnerSubscriber.firstName) ??
    toText(requestSubscriberPayload.firstName) ??
    toText(requestPayloadInnerSubscriber.firstName) ??
    toText(requestPayload.first_name) ??
    toText(requestPayload.firstName) ??
    toText(requestPayloadInner.first_name) ??
    toText(requestPayloadInner.firstName) ??
    toText(responsePayloadInner.first_name) ??
    toText(responsePayloadInner.firstName) ??
    toText(responseInner.first_name) ??
    toText(responseInner.firstName) ??
    toText(responseInner.subscriber_first_name) ??
    toText(responsePayload.first_name) ??
    'Unknown'

  const lastName =
    toText(check.last_name) ??
    toText(check.patient_last_name) ??
    toText(check.subscriber_last_name) ??
    toText(responseSubscriber.lastName) ??
    toText(responseSubscriberPayload.lastName) ??
    toText(responsePayloadInnerSubscriber.lastName) ??
    toText(requestSubscriberPayload.lastName) ??
    toText(requestPayloadInnerSubscriber.lastName) ??
    toText(requestPayload.last_name) ??
    toText(requestPayload.lastName) ??
    toText(requestPayloadInner.last_name) ??
    toText(requestPayloadInner.lastName) ??
    toText(responsePayloadInner.last_name) ??
    toText(responsePayloadInner.lastName) ??
    toText(responseInner.last_name) ??
    toText(responseInner.lastName) ??
    toText(responseInner.subscriber_last_name) ??
    toText(responsePayload.last_name) ??
    'Patient'

  const id = toText(check.id) ?? `eligibility-check-${index + 1}`
  const patientBackendId =
    toText(check.patient_id) ??
    toText(requestPayload.patient_id) ??
    toText(requestPayload.patientId) ??
    undefined
  const patientId =
    patientBackendId ??
    toText(check.patient_intake_id) ??
    id

  const normalizedDateOfBirth =
    toDateText(check.date_of_birth) ??
    toDateText(check.dateOfBirth) ??
    toDateText(check.dob) ??
    toDateText(check.patient_dob) ??
    toDateText(check.subscriber_dob) ??
    toDateText(responseSubscriber.dateOfBirth) ??
    toDateText(responseSubscriberPayload.dateOfBirth) ??
    toDateText(responsePayloadInnerSubscriber.dateOfBirth) ??
    toDateText(requestSubscriberPayload.dateOfBirth) ??
    toDateText(requestPayloadInnerSubscriber.dateOfBirth) ??
    toDateText(requestPayload.date_of_birth) ??
    toDateText(requestPayload.dateOfBirth) ??
    toDateText(requestPayload.dob) ??
    toDateText(requestPayloadInner.date_of_birth) ??
    toDateText(requestPayloadInner.dateOfBirth) ??
    toDateText(requestPayloadInner.dob) ??
    toDateText(requestPatientPayload.date_of_birth) ??
    toDateText(requestPatientPayload.dateOfBirth) ??
    toDateText(requestPatientPayload.dob) ??
    toDateText(requestSubscriberPayload.date_of_birth) ??
    toDateText(requestSubscriberPayload.dateOfBirth) ??
    toDateText(requestSubscriberPayload.dob) ??
    toDateText(requestPayloadInnerPatient.date_of_birth) ??
    toDateText(requestPayloadInnerPatient.dateOfBirth) ??
    toDateText(requestPayloadInnerPatient.dob) ??
    toDateText(responsePayload.date_of_birth) ??
    toDateText(responsePayload.dateOfBirth) ??
    toDateText(responsePayload.dob) ??
    toDateText(responsePatientPayload.date_of_birth) ??
    toDateText(responsePatientPayload.dateOfBirth) ??
    toDateText(responsePatientPayload.dob) ??
    toDateText(responseSubscriberPayload.date_of_birth) ??
    toDateText(responseSubscriberPayload.dateOfBirth) ??
    toDateText(responseSubscriberPayload.dob) ??
    toDateText(responsePayloadInnerPatient.date_of_birth) ??
    toDateText(responsePayloadInnerPatient.dateOfBirth) ??
    toDateText(responsePayloadInnerPatient.dob) ??
    toDateText(responseInner.date_of_birth) ??
    toDateText(responseInner.dateOfBirth) ??
    toDateText(responseInner.dob)

  const eligibilityStatus = check.eligibility_display ?? check.status
  const patientStatus = check.status
  const patientFullName = `${firstName} ${lastName}`.trim()
  const pverifySubscriberNameText = pverifySubscriberName(pverifySubscriber)
  const inferredRelationship =
    pverifySubscriberNameText &&
    patientFullName &&
    pverifySubscriberNameText.toLowerCase() === patientFullName.toLowerCase()
      ? 'Self'
      : undefined

  const eligibility = normalizeEligibility(eligibilityStatus)
  const status = normalizePatientStatus(patientStatus)

  const record: PatientRecord = {
    id,
    patientId,
    patientBackendId,
    firstName,
    lastName,
    dateOfBirth: normalizedDateOfBirth ?? '',
    primaryPayor:
      toText(check.payer_name) ??
      toText(check.payor_name) ??
      toText(responseInnerPayer.name) ??
      toText(responsePayer.name) ??
      toText(responsePayloadInnerPayer.name) ??
      toText(requestPayer.name) ??
      toText(requestPayloadInnerPayer.name) ??
      toText(requestPayload.payer_name) ??
      toText(requestPayload.payor_name) ??
      toText(requestPayloadInner.payer_name) ??
      toText(requestPayloadInner.payor_name) ??
      toText(responsePayload.payer_name) ??
      toText(responsePayload.payor_name) ??
      toText(responsePayloadInner.payer_name) ??
      toText(responsePayloadInner.payor_name) ??
      toText(responseInner.payer_name) ??
      toText(responseInner.payor_name) ??
      toText(requestPayload.payor) ??
      'Unknown payor',
    memberId:
      toText(check.member_id) ??
      toText(responseSubscriber.memberId) ??
      toText(responseSubscriberPayload.memberId) ??
      toText(responsePayloadInnerSubscriber.memberId) ??
      toText(requestSubscriberPayload.memberId) ??
      toText(requestPayloadInnerSubscriber.memberId) ??
      toText(requestPayload.member_id) ??
      toText(requestPayload.subscriber_id) ??
      toText(requestPayload.subscriberId) ??
      toText(requestPayloadInner.member_id) ??
      toText(requestPayloadInner.memberId) ??
      toText(requestPayloadInner.subscriber_id) ??
      toText(requestPayloadInner.subscriberId) ??
      toText(responsePayload.member_id) ??
      toText(responsePayload.memberId) ??
      toText(responsePayloadInner.member_id) ??
      toText(responsePayloadInner.memberId) ??
      toText(responsePayloadInner.subscriber_id) ??
      toText(responsePayloadInner.subscriberId) ??
      toText(responseInner.member_id) ??
      toText(responseInner.memberId) ??
      'N/A',
    lastVerified:
      toDateText(check.last_verified_display) ??
      toDateText(check.checked_at) ??
      toDateText(check.stedi_api_date) ??
      toDateText(check.api_date) ??
      new Date().toISOString().slice(0, 10),
    eligibility,
    status,
    sex:
      normalizeGender(check.gender) ??
      normalizeGender(check.patient_gender) ??
      normalizeGender(responsePatient.gender) ??
      normalizeGender(responseSubscriber.gender) ??
      normalizeGender(responseSubscriberPayload.gender) ??
      normalizeGender(responsePayloadInnerSubscriber.gender) ??
      normalizeGender(requestPatientPayload.gender) ??
      normalizeGender(requestSubscriberPayload.gender) ??
      normalizeGender(pverifySubscriber.Gender) ??
      normalizeGender(pverifySubscriber.GenderCode) ??
      normalizeGender(pverifySubscriber.Sex),
    address:
      formatAddress(subscriberAddress) ??
      formatPverifyAddress(pverifySubscriber) ??
      toText(check.address),
    plan:
      pickField(responseSubscriber, 'groupDescription') ??
      pickField(planInformation, 'groupDescription', 'planDescription') ??
      firstPlanDetail(responseInner) ??
      toText(pverifyPlanSummary.PlanName) ??
      toText(pverifyPlanSummary.PolicyType) ??
      toText(pverifyPlanSummary.PlanType) ??
      pickField(responseSubscriber, 'planNumber') ??
      pickField(planInformation, 'planNumber') ??
      pickField(check, 'plan', 'plan_name', 'planName'),
    groupNumber:
      pickField(responseSubscriber, 'groupNumber') ??
      pickField(planInformation, 'groupNumber') ??
      toText(pverifyPlanSummary.GroupNumber) ??
      pickField(check, 'group_number', 'groupNumber', 'patient_intake_id'),
    coverageEffective:
      toDateText(pverifyPlanSummary.EffectiveDate) ??
      toDateText(planDateInformation.eligibilityBegin) ??
      toDateText(planDateInformation.planBegin) ??
      toDateText(planDateInformation.policyEffective),
    coverageEnd:
      toDateText(pverifyPlanSummary.TerminationDate) ??
      toDateText(pverifyPlanSummary.ExpiryDate) ??
      toDateText(pverifyPlanSummary.EndDate) ??
      toDateText(planDateInformation.eligibilityEnd) ??
      toDateText(planDateInformation.planEnd) ??
      toDateText(planDateInformation.policyExpiration),
    subscriberRelationship:
      normalizeRelationship(pverifySubscriber.RelationShip) ??
      normalizeRelationship(pverifySubscriber.Relationship) ??
      normalizeRelationship(pverifySubscriber.Relation) ??
      normalizeRelationship(responseSubscriber.entityIdentifier) ??
      normalizeRelationship(requestPayload.relationToSubscriber) ??
      normalizeRelationship(requestPayloadInner.relationToSubscriber) ??
      normalizeRelationship(check.subscriber_relationship) ??
      inferredRelationship,
    coordinationOfBenefits:
      normalizeCoordinationOfBenefits(pverifyPlanSummary.CoordinationOfBenefits) ??
      normalizeCoordinationOfBenefits(pverifyPlanSummary.COB) ??
      normalizeCoordinationOfBenefits(check.coordination_of_benefits) ??
      (normalizeEligibility(eligibilityStatus) === 'verified' ? 'Primary' : undefined),
    doctorName: toText(responseProvider.providerName),
    doctorNpi: toText(responseProvider.npi),
  }

  record.priorAuthGridStatus = resolvePriorAuthGridStatus(record)
  return record
}
